import {
  DEFAULT_AGENT_COMPONENT_PAGE_SIZE,
  type AgentComponent,
  type AgentComponentPage,
  type AgentComponentType,
} from '@reach/shared-types/agent-components';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  agentComponentKeys,
  fetchAgentComponent,
  fetchAgentComponentPage,
  fetchAgentComponentToolOptions,
} from './agent-components-api';

export const STANDARD_CACHE = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: true,
} as const;

/** The partner's skills, or its guidance, alphabetically by name, loaded one page at a time. */
export function useAgentComponentList(type: AgentComponentType) {
  return useInfiniteQuery({
    queryKey: agentComponentKeys.list(type),
    queryFn: ({ pageParam }) =>
      fetchAgentComponentPage({ type, limit: DEFAULT_AGENT_COMPONENT_PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: AgentComponentPage) => lastPage.pagination.cursor ?? undefined,
    select: componentPages => componentPages.pages.flatMap(page => page.results),
    ...STANDARD_CACHE,
  });
}

/** One saved skill or guidance for its component page; null when there is no such component of this type. */
export function useAgentComponent(type: AgentComponentType, componentId: string) {
  return useQuery({
    queryKey: agentComponentKeys.detail(componentId),
    queryFn: () => fetchAgentComponent(componentId),
    select: (component: AgentComponent | null) => {
      const componentIsOfType = component?.type === type;
      return componentIsOfType ? component : null;
    },
    ...STANDARD_CACHE,
  });
}

/** The partner's active tools for a component's tool picker, with how many components use each. */
export function useAgentComponentToolOptions() {
  return useQuery({
    queryKey: agentComponentKeys.toolOptions(),
    queryFn: fetchAgentComponentToolOptions,
    ...STANDARD_CACHE,
    refetchOnMount: 'always',
  });
}

/** How many skills, or guidance items, the partner has. */
export function useAgentComponentCount(type: AgentComponentType) {
  return useQuery({
    queryKey: agentComponentKeys.count(type),
    queryFn: () => fetchAgentComponentPage({ type, limit: 1 }),
    select: (page: AgentComponentPage) => page.pagination.total,
    ...STANDARD_CACHE,
  });
}
